import type React from "react";

import type { AppTicketDto } from "@/api/types";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { User } from "@/components/User/User";
import { Card } from "@/design-system/Card";
import { Spread } from "@/design-system/Spread";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { RuntimeColorBadge } from "@/ui-adapters/RuntimeColorBadge";

interface CreateTicketCollectiveTicketCardProps {
  ticket: AppTicketDto;
}

export function CreateTicketCollectiveTicketCard({ ticket }: CreateTicketCollectiveTicketCardProps): React.ReactNode {
  const { i18n } = useTranslation();
  const slug = useSlug();

  const handleOpenTicketDetails = () => {
    const windowWidth = 480;
    const windowHeight = windowWidth * (4 / 3); // 4:3I aspect ratio

    window.open(
      routes.tickets.details({
        slug,
        id: ticket.id,
      }),
      "",
      `width=${windowWidth},height=${windowHeight},resizable=yes,scrollbars=yes,status=yes`,
    );
  };

  return (
    <Card
      as="button"
      type="button"
      title="Open ticket details in a new window"
      onClick={handleOpenTicketDetails}
      borderTone="secondary"
      interactive
    >
      <Spread space="md" align="start">
        <User
          user={ticket.user}
          renderSubtitle={() => formatDate(i18n, "datetime", ticket.postedAt)}
          shouldNavigate={false}
        />
        <RuntimeColorBadge color={ticket.status.color}>{ticket.status.name}</RuntimeColorBadge>
      </Spread>

      <span className="text-body-emphasis">{ticket.title}</span>
    </Card>
  );
}
